import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Container,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) dispatch(clearError());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const result = await dispatch(loginUser(formData));
      if (loginUser.fulfilled.match(result)) {
        toast.success('Login successful!');
        navigate('/');
      } else if (loginUser.rejected.match(result)) {
        toast.error(result.payload as string);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Ubuntu, Arial, sans-serif',
      }}
    >
      <Container maxWidth="lg" sx={{ px: 2 }}>
        <Grid container spacing={4} alignItems="center" sx={{ minHeight: '100vh' }}>
          {/* Left Section - Illustration */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* Main Illustration Image */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  height: 600,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src="/login.png"
                  alt="Login Illustration"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '12px',
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Section - Login Form */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Logo */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <img
                    src="/logo.png"
                    alt="E7 Logo"
                    style={{
                      maxWidth: '80px',
                      height: 'auto',
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      letterSpacing: '0.1em',
                      fontFamily: 'Ubuntu, Arial, sans-serif',
                    }}
                  >
                    GROUP
                  </Typography>
                </Box>
              </Box>

              {/* Main Title */}
              <Typography
                variant="h4"
                sx={{
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  textAlign: 'center',
                  mb: 4,
                }}
              >
                Sign In and Take Control
              </Typography>

              {/* Login Form Card */}
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  borderRadius: 3,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  backgroundColor: 'white',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#374151',
                      fontWeight: 600,
                      mb: 3,
                      textAlign: 'center',
                    }}
                  >
                    Please fill the form
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    {error && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          backgroundColor: '#fee2e2',
                          borderRadius: 2,
                          border: '1px solid #fca5a5',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#dc2626', textAlign: 'center' }}>
                          {error}
                        </Typography>
                      </Box>
                    )}

                    {/* Username Field */}
                    <TextField
                      fullWidth
                      name="username"
                      label="Username"
                      placeholder="Enter username"
                      margin="normal"
                      required
                      value={formData.username}
                      onChange={handleInputChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#e5e7eb',
                          },
                          '&:hover fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#0d9488',
                          },
                        },
                      }}
                    />

                    {/* Password Field */}
                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      placeholder="********"
                      type={showPassword ? 'text' : 'password'}
                      margin="normal"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: '#6b7280' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#e5e7eb',
                          },
                          '&:hover fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#0d9488',
                          },
                        },
                      }}
                    />

                    {/* Sign In Button */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        mt: 3,
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 6px -1px rgba(13, 148, 136, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                          boxShadow: '0 6px 8px -1px rgba(13, 148, 136, 0.4)',
                        },
                        '&:disabled': {
                          background: '#9ca3af',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
          </Grid>
        </Container>
        
        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Box>
    );
  };

  export default Login;